
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

const Login = () => {  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setApiError(null);

    try {
      await login(email, password);
    } catch (error: any) {
      console.error("Login error in component:", error);
      const errorMessage = error?.message || 'Unknown error occurred';
      
      if (errorMessage.includes('timeout') || errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
        setApiError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (errorMessage.includes('Invalid email or password')) {
        toast({
          title: "Login Failed",
          description: "Invalid email or password. Please check your credentials.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Login Failed", 
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="max-w-md w-full space-y-8">
        <div className="flex justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve" x="0" y="0" version="1.1" viewBox="0 0 471.9 123.4" className="h-16 w-auto fill-foreground">
            <path d="M296.1 26.6c-3.1.2-6.2 0-9.5 0v-10c3.2-1.2 5.5-4.4 5.5-8 0-4.7-3.8-8.6-8.6-8.6-4.7 0-8.6 3.8-8.6 8.6 0 3.7 2.4 6.9 5.7 8.1v10h-15.4c-6.9.1-13.5 1.5-19.3 5.5-10.9 7.6-13.6 18.4-13.4 31 .4 19.9 12.9 32.9 32.7 33 12.3.1 24.6.2 37-.1 4.3-.1 8.7-.8 12.7-2.2 11.9-4.2 18.9-15.7 19.8-28.5 1.7-26.7-14.5-40.7-38.6-38.8zm25 39.9c-.8 9.1-7 14.8-16.2 14.8h-21.5c-7.3 0-14.7.1-22 0-8-.1-13.8-5.2-15.2-13.1-.2-1.3-.3-2.6-.4-4-1.1-15.4 4.3-23.9 22.4-23.1 12 .6 24 .1 36 .1 10.4.1 16.8 6.3 17.1 16.7 0 3 0 5.8-.2 8.6zm-22.8 35.9c-5.3 2.4-9.9 4.4-14.4 6.4-.3.1-.6.1-.8 0-4.6-2.1-9.2-4.1-14.4-6.4-.7-.3-1.4.2-1.4.9v19.1c0 .7.8 1.2 1.4.9 5.3-2.3 9.9-4.4 14.5-6.4.3-.1.6-.1.8 0 4.7 2.1 9.2 4.1 14.4 6.4.7.3 1.4-.2 1.4-.9v-19.1c-.1-.8-.8-1.2-1.5-.9z"/>
            <path d="M300.8 54.8c-1.8 0-3.4.7-4.7 1.9l-.3.3c-1.1 1.2-1.7 2.8-1.7 4.5 0 3.7 3 6.7 6.7 6.7s6.7-3 6.7-6.7-3-6.7-6.7-6.7z"/>
            <circle cx="266.2" cy="61.5" r="6.7"/>
            <path d="M68.2 29.5h15.4v65.1H68.4l-.8-9.6c-3.7 7.7-13.9 11.4-21.1 11.5C27.3 96.7 13 84.8 13 61.9c0-22.4 14.9-34.2 33.9-34.1 8.7 0 17 4.1 20.7 10.6l.6-8.9zM29.1 61.9c0 12.4 8.6 19.8 19.3 19.8 25.3 0 25.3-39.5 0-39.5-10.7.1-19.3 7.3-19.3 19.7zM91 44.6v28.1c0 16.2 9.2 24.3 23.4 23.8 2.7-.1 5-.3 7.4-.9v-15c-2 .8-4.3 1.3-6.3 1.3-5.1-.1-8.5-3.1-8.5-9.3V44.5h13.3V30.6h-13.2V13.9H91v30.7zm93.9-15.3 14.3 49 14.9-49h17.8l-22.7 65.5h-18.7l-6.7-19.3-5.8-21.8-5.8 21.8-6.7 19.3h-18.7L124 29.3h18l14.9 49 14.1-49h13.9zm175 .2 1.2 7.5c5-8.1 11.7-9.2 18.3-9.2 6.7 0 13.2 2.6 16.8 6.2L389 48c-3.3-2.8-6.3-4.2-11.6-4.2-8.4 0-16.2 4.5-16.2 16.5v34.3H345V29.5h14.9zM418 2.3v53.3l21.9-26.1h19.3v.9l-26.5 29.7 30.2 33.3v1.2h-19.4L418 65.1v29.4h-16.1V2.3H418z"/>
          </svg>
        </div>
        
        <Card className="border border-border shadow-lg bg-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-card-foreground">Welcome back</CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              Enter your credentials to sign in to your account
            </CardDescription>
            {apiError && (
              <div className="p-3 mt-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                <strong>Connection Error:</strong> {apiError}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="email"
                    placeholder="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="password"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => 
                      setRememberMe(checked as boolean)
                    }
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground"
                  >
                    Remember me
                  </label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium text-primary hover:underline"
                  tabIndex={isLoading ? -1 : 0}
                >
                  Forgot password?
                </Link>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90" 
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link 
                to="/register" 
                className="text-primary font-medium hover:underline"
                tabIndex={isLoading ? -1 : 0}
              >
                Sign up
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Login;
